/**
 * Species profiles for the Forge Score Optimizer.
 *
 * Each species defines:
 *   - the typical range it's hunted at (used for "score at this range")
 *   - a mass-class threshold (heavier game → momentum matters more)
 *   - a shot scenario weighting bias (cape buffalo = expect quartering-into,
 *     target = always broadside)
 *   - a "sweet spot" Forge Score band that flags arrow weights as ideal
 *     for that species
 *
 * Calibrated against Sal's documented field anchors where possible.
 */

import type { ShotScenario, SpeciesTag } from '../trajectory/types';

export interface SpeciesProfile {
  tag: SpeciesTag;
  label: string;
  category: 'whitetail' | 'western' | 'plains-game' | 'big-game' | 'target' | 'small-game';
  /** Typical shot range users will plug in */
  typicalRangeYards: number;
  /** Game-mass threshold class (informational + drives score curve shape) */
  gameMassClass: 'small' | 'mid' | 'large' | 'very-large' | 'dangerous';
  /** Likely shot scenario the user is preparing for (the most common) */
  expectedShotScenario: ShotScenario;
  /** Bias added to the momentum score weight for this species (0 = neutral) */
  momentumBias: number;
  /** Bias added to the energy-retention weight (long-range species higher) */
  retentionBias: number;
  /** Sweet spot Forge Score threshold — arrow weights at or above this Forge
   *  Score for this species are considered "in the sweet spot" */
  sweetSpotMinScore: number;
  /**
   * Hard minimum arrow weight (grains) for this species. When set, the
   * optimizer refuses to recommend any build below this weight as "best"
   * — even if the un-constrained score peaks lower. The chart still
   * renders the full curve below the floor in greyed-out form so the
   * user understands WHY the floor exists.
   *
   * Used for dangerous game (cape buffalo, etc.) where mass × velocity
   * × bone-defeating geometry is non-negotiable and a low-weight winner
   * on the math would be irresponsible advice.
   */
  minArrowGrains?: number;
  /**
   * Plain-English reason for the minArrowGrains floor — surfaced in the
   * optimizer UI next to the floor marker. Required if minArrowGrains is set.
   */
  minArrowGrainsReason?: string;
  /** Field Notes / Methods anchor reference (visible when species is chosen) */
  anchor?: string;
}

export const SPECIES_PROFILES: ReadonlyArray<SpeciesProfile> = [
  {
    tag: 'whitetail-stand',
    label: 'Whitetail · stand',
    category: 'whitetail',
    typicalRangeYards: 22,
    gameMassClass: 'mid',
    expectedShotScenario: 'broadside-near',
    momentumBias: 0,
    retentionBias: -5,
    sweetSpotMinScore: 80,
  },
  {
    tag: 'whitetail-spot-stalk',
    label: 'Whitetail · spot-stalk',
    category: 'whitetail',
    typicalRangeYards: 35,
    gameMassClass: 'mid',
    expectedShotScenario: 'broadside-far',
    momentumBias: 2,
    retentionBias: 0,
    sweetSpotMinScore: 82,
  },
  {
    tag: 'mule-deer-western',
    label: 'Mule deer · western',
    category: 'western',
    typicalRangeYards: 50,
    gameMassClass: 'mid',
    expectedShotScenario: 'quartering-away',
    momentumBias: 3,
    retentionBias: 5,
    sweetSpotMinScore: 85,
  },
  {
    tag: 'elk-western',
    label: 'Elk · western',
    category: 'western',
    typicalRangeYards: 60,
    gameMassClass: 'large',
    expectedShotScenario: 'broadside-far',
    momentumBias: 6,
    retentionBias: 8,
    sweetSpotMinScore: 87,
  },
  {
    tag: 'impala',
    label: 'Impala',
    category: 'plains-game',
    typicalRangeYards: 30,
    gameMassClass: 'mid',
    expectedShotScenario: 'quartering-away',
    momentumBias: 0,
    retentionBias: 0,
    sweetSpotMinScore: 80,
    anchor: 'String-jump risk above 35y at 280fps — see Field Notes #5.',
  },
  {
    tag: 'kudu',
    label: 'Kudu',
    category: 'plains-game',
    typicalRangeYards: 40,
    gameMassClass: 'large',
    expectedShotScenario: 'quartering-away',
    momentumBias: 5,
    retentionBias: 3,
    sweetSpotMinScore: 85,
  },
  {
    tag: 'eland',
    label: 'Eland',
    category: 'plains-game',
    typicalRangeYards: 40,
    gameMassClass: 'very-large',
    expectedShotScenario: 'broadside-near',
    momentumBias: 12,
    retentionBias: 0,
    sweetSpotMinScore: 88,
    minArrowGrains: 500,
    minArrowGrainsReason: 'Eland are the largest plains-game antelope (1,500-2,200 lb). Zimbabwe Class B and South Africa Category 3 both set the legal minimum at 500 grains and 60-65 lb draw weight. Lighter arrows will not reliably reach vitals on a heavy-bone quartering shot.',
    anchor: 'See Methods §12 — 15% FOC mech almost passed; tune-quality dominant. ZW Class B / SA Cat 3 minimum.',
  },
  {
    tag: 'cape-buffalo',
    label: 'Cape buffalo',
    category: 'big-game',
    typicalRangeYards: 25,
    gameMassClass: 'dangerous',
    expectedShotScenario: 'broadside-near',
    momentumBias: 20,
    retentionBias: -10,
    sweetSpotMinScore: 90,
    minArrowGrains: 750,
    minArrowGrainsReason: 'Cape buffalo kill more hunters than any other African game. South Africa Category 4 (gazetted) and Zimbabwe Class A both set the statutory minimum at 750-grain arrow, 80 lb draw weight, 80 ft-lb kinetic energy, fixed 2-blade cut-on-contact broadhead. Outfitter "ethical floor" is closer to 850 grains. The Forge will not recommend any arrow under 750gr for this animal — the math is non-negotiable and the consequence of an under-penetrating shot is fatal to the hunter.',
    anchor: 'PHASA / SA Cat 4 + ZW Class A statutory floor. Mass + single-bevel COC. No mechs above 8° impact yaw.',
  },
  {
    tag: 'hippo',
    label: 'Hippo',
    category: 'big-game',
    typicalRangeYards: 25,
    gameMassClass: 'dangerous',
    expectedShotScenario: 'broadside-near',
    momentumBias: 25,
    retentionBias: -10,
    sweetSpotMinScore: 92,
    minArrowGrains: 750,
    minArrowGrainsReason: 'Hippo are the deadliest large mammal in Africa — they kill more humans annually than lions, leopards, or buffalo. Heavy hide + 1.5" rib bone + thick muscle mean penetration is everything. South Africa Category 4 sets the statutory minimum at 750-grain arrow, 80 lb draw, 80 ft-lb KE, fixed 2-blade COC. Hippo bowhunting is legal only in South Africa and Mozambique. The Forge enforces the statutory floor.',
    anchor: 'SA Cat 4 statutory floor. Only legal by bow in South Africa + Mozambique.',
  },
  {
    tag: 'elephant',
    label: 'Elephant',
    category: 'big-game',
    typicalRangeYards: 25,
    gameMassClass: 'dangerous',
    expectedShotScenario: 'broadside-near',
    momentumBias: 30,
    retentionBias: -15,
    sweetSpotMinScore: 95,
    minArrowGrains: 850,
    minArrowGrainsReason: 'Bow-hunting elephant is prohibited by law in South Africa, Namibia, Tanzania, and Botswana. Where legal (Zimbabwe Class A, Mozambique by outfitter discretion), Zimbabwe sets the statutory minimum at 700 grains / 80 lb draw weight, but the outfitter and ethical consensus across both countries is 850-1000 grains and 90 lb draw, paired with a fixed 2-blade COC steel broadhead. The Forge enforces 850gr as the practical floor — below that, the math simply does not put the arrow through the rib cage of a 6,000-12,000 lb animal at any reasonable angle.',
    anchor: 'ZW Class A statutory + ethical floor consensus. Prohibited in SA / Namibia / Tanzania / Botswana.',
  },
  {
    tag: 'red-stag',
    label: 'Red stag',
    category: 'big-game',
    typicalRangeYards: 40,
    gameMassClass: 'large',
    expectedShotScenario: 'quartering-away',
    momentumBias: 6,
    retentionBias: 3,
    sweetSpotMinScore: 85,
    anchor: 'See Field Notes #2 — 460gr BEAST mech, quartering-away + 30° downhill = full pen.',
  },
  {
    tag: 'pronghorn',
    label: 'Pronghorn',
    category: 'western',
    typicalRangeYards: 60,
    gameMassClass: 'small',
    expectedShotScenario: 'broadside-far',
    momentumBias: -3,
    retentionBias: 8,
    sweetSpotMinScore: 82,
  },
  {
    tag: 'hog',
    label: 'Hog',
    category: 'whitetail',
    typicalRangeYards: 25,
    gameMassClass: 'mid',
    expectedShotScenario: 'broadside-near',
    momentumBias: 4,
    retentionBias: -5,
    sweetSpotMinScore: 80,
  },
  {
    tag: '3D-target',
    label: '3D / target',
    category: 'target',
    typicalRangeYards: 50,
    gameMassClass: 'small',
    expectedShotScenario: 'broadside-near',
    momentumBias: -8,
    retentionBias: 12,
    sweetSpotMinScore: 78,
  },
];

export function getSpecies(tag: SpeciesTag): SpeciesProfile | undefined {
  return SPECIES_PROFILES.find((s) => s.tag === tag);
}
